<template>
  <div class="input-radio" :class="classes" @click="clickEvent">
    <cv-radio-button
      :id="id"
      v-model="selectedValueInner"
      :name="name"
      :value="value"
      :disabled="disabled"
      :checked="isSelected"
      @change="changeEvent"
    ></cv-radio-button>
    <label :for="id">
      <!-- Avoid usage of "icon" as class here as it will be affected by 
           generic styling for icon across the system -->
      <span class="input-icon">
        <svg-icon :name="icon" kind="forms" />
      </span>
      <span class="text">
        <slot></slot>
      </span>
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

// Props
interface Props {
  id?: string;
  name?: string;
  disabled?: boolean;
  value?: string;
  selectedValue?: string;
  type?: string;
  kind?: string; // Named as "kind" to follow Carbon naming convention for bx-tag colors
}

const {
  id = "0",
  name = "radio-button",
  disabled = false,
  value = "",
  selectedValue = "",
  type = "radio",
  kind = "grey",
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  click: [data: unknown];
  change: [data: string];
  "update:selectedValue": [data: unknown];
}>();

// Data
const selectedValueInner = ref(selectedValue);

// Computed
const isSelected = computed(() => {
  return selectedValueInner.value === value;
});

const classes = computed(() => {
  return [
    `input-radio-${kind}`,
    {
      disabled: disabled,
      selected: isSelected.value,
    },
  ];
});

const icon = computed(() => {
  if (isSelected.value) {
    return type === "tick" ? "RadioTicked24" : "RadioChecked24";
  }
  return "Radio24";
});

// Methods
const changeEvent = (value: string) => {
  emit("update:selectedValue", value);
  emit("change", value);
};

const clickEvent = (event) => {
  emit("click", event);
};

const validateType = (value) => {
  return ["radio", "tick"].includes(value);
};

// Watch
watch(
  () => selectedValue,
  () => {
    selectedValueInner.value = selectedValue;
  },
);

watch(
  () => type,
  () => {
    validateType(type);
  },
);
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.input-radio {
  display: flex;
  align-items: center;
  margin: $spacing-03 0;
  word-break: break-word;
  border: 1px solid transparent;
  border-radius: 10px;

  &.input-radio-white {
    &:not(.selected) {
      > label {
        > .input-icon {
          rect {
            fill: $ui-01;
          }
        }
      }
    }
  }

  &.disabled {
    &.selected {
      .input-icon {
        .sls-icon {
          &:not([name="RadioTicked24"]) {
            rect {
              stroke: $disabled-02;
            }
          }
        }
      }
    }

    label {
      color: $disabled-02;
      cursor: default;
      pointer-events: none;

      .input-icon {
        rect {
          stroke: transparent;
        }

        circle {
          fill: $disabled-02;
        }
      }

      .rich-text-editor {
        color: $text-01;
      }

      .text {
        pointer-events: all; // To allow scrolling of the text when there is scrollbar
      }
    }
  }

  // Radio button aligned top
  > .bx--radio-button-wrapper {
    margin-top: $spacing-01;
    margin-bottom: auto;

    // Hide carbon default radio to use our own customization
    .bx--radio-button__label {
      display: none;
    }
  }

  > label {
    display: flex;
    // To align top intentionally according to designer instead of center
    align-items: flex-start;
    flex: 0 0 100%;
    max-width: 100%;
    cursor: pointer;

    > .input-icon {
      width: 24px;
      height: 24px;
      margin-right: $spacing-03;
    }

    .text {
      @include carbon--type-style("body-short-01");
      flex: 1 1 0%;
      margin-top: $spacing-01;
    }
  }
}
</style>
